<template>
  <main-template>
    <v-row class="mb-2">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Justificar Atraso
        </h1>
        <p class="body-2 ml-8 mb-0">
          {{
            podeEscolherColaborador
              ? "Escolha o atraso e informe o motivo. Você pode justificar o seu ou o de um colaborador."
              : "Escolha o atraso e informe o motivo da justificativa."
          }}
        </p>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-title class="subtitle-1 font-weight-bold">Dados da justificativa</v-card-title>
      <v-divider />
      <v-card-text class="pt-5">
        <v-form ref="formRef">
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                v-if="podeEscolherColaborador"
                v-model="colaboradorSelecionado"
                :items="colaboradores"
                :loading="carregandoColaboradores"
                item-text="name"
                return-object
                label="Colaborador"
                placeholder="Você mesmo"
                hint="Escolha um colaborador para lançar a justificativa em nome dele."
                persistent-hint
                outlined
                dense
                clearable
                @change="carregarPendentes"
              />
              <v-text-field
                v-else
                :value="user && user.name"
                label="Colaborador"
                outlined
                dense
                readonly
                background-color="grey lighten-4"
              />
            </v-col>

            <v-col v-if="colaboradorSelecionado" cols="12">
              <v-alert type="info" text dense class="mb-2">
                A justificativa de <strong>{{ colaboradorSelecionado.name }}</strong> será
                registrada já aprovada por você.
              </v-alert>
            </v-col>

            <v-col cols="12" class="mt-2">
              <div class="subtitle-2 font-weight-bold mb-1">Atraso a justificar *</div>
              <div class="caption text--secondary mb-3">
                Atrasos sem justificativa nos últimos 30 dias.
              </div>

              <v-row v-if="carregandoPendentes" dense>
                <v-col v-for="n in 3" :key="n" cols="12" sm="6" md="4">
                  <v-skeleton-loader type="list-item-two-line" class="rounded-lg" />
                </v-col>
              </v-row>

              <v-alert
                v-else-if="pendentes.length === 0"
                type="success"
                text
                dense
                icon="mdi-check-circle-outline"
              >
                Nenhum atraso pendente de justificativa nos últimos 30 dias.
              </v-alert>

              <v-item-group v-else v-model="atrasoSelecionado" mandatory>
                <v-row dense>
                  <v-col
                    v-for="atraso in pendentes"
                    :key="atraso.colaborador_batida_id"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-item v-slot="{ active, toggle }" :value="atraso">
                      <v-card
                        outlined
                        class="atraso-card"
                        :class="{ 'atraso-card--ativo': active }"
                        @click="toggle"
                      >
                        <v-card-text class="py-3">
                          <div class="d-flex align-center justify-space-between mb-2">
                            <div>
                              <div class="subtitle-2 font-weight-bold">
                                {{ atraso.data_formatada }}
                              </div>
                              <div class="caption text--secondary text-capitalize">
                                {{ diaDaSemana(atraso.data) }}
                              </div>
                            </div>
                            <v-icon :color="active ? 'primary' : 'grey lighten-1'">
                              {{ active ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
                            </v-icon>
                          </div>
                          <div class="d-flex align-center flex-wrap caption">
                            <span class="mr-3">
                              Previsto <strong>{{ atraso.hora_prevista }}</strong>
                            </span>
                            <span class="mr-3">
                              Registrado
                              <strong class="error--text">{{ atraso.hora_registrada }}</strong>
                            </span>
                            <v-chip x-small color="warning" class="font-weight-bold white--text">
                              {{ atraso.tempo_atraso }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-item>
                  </v-col>
                </v-row>
              </v-item-group>
            </v-col>

            <v-col cols="12" class="mt-4">
              <v-textarea
                v-model="form.motivo"
                label="Motivo *"
                placeholder="Descreva o motivo do atraso"
                outlined
                dense
                rows="3"
                counter="500"
                :disabled="!atrasoSelecionado"
                :rules="[
                  (v) => !!(v && v.trim()) || 'Motivo obrigatório',
                  (v) => !v || v.trim().length >= 5 || 'Informe pelo menos 5 caracteres',
                  (v) => !v || v.length <= 500 || 'Máximo 500 caracteres',
                  validarTexto,
                ]"
              />
            </v-col>

            <v-col cols="12">
              <v-file-input
                v-model="form.anexo"
                label="Anexar documento (PDF, JPEG, PNG - máx. 10MB)"
                outlined
                dense
                show-size
                accept=".pdf,.jpg,.jpeg,.png"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                clearable
                :disabled="!atrasoSelecionado"
                :rules="[validarArquivo]"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text :disabled="enviando" @click="$router.go(-1)">Cancelar</v-btn>
        <v-btn
          color="primary"
          :loading="enviando"
          :disabled="!podeEnviar || enviando"
          @click="enviar"
        >
          <v-icon left small>mdi-send</v-icon>
          Enviar Justificativa
        </v-btn>
      </v-card-actions>
    </v-card>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";

const ROLES_ADMIN = ["admin", "super-admin", "TI"];

export default {
  name: "NovoAtrasoJustificadoPage",
  data() {
    return {
      colaboradores: [],
      colaboradorSelecionado: null,
      carregandoColaboradores: false,
      pendentes: [],
      carregandoPendentes: false,
      atrasoSelecionado: null,
      enviando: false,
      form: {
        motivo: "",
        anexo: null,
      },
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    // Administrador pode justificar o atraso de um colaborador (entra aprovado).
    podeEscolherColaborador() {
      return ROLES_ADMIN.includes(this.user?.roleName);
    },
    podeEnviar() {
      const motivo = this.form.motivo?.trim() || "";
      if (!this.atrasoSelecionado || motivo.length < 5 || this.form.motivo.length > 500) {
        return false;
      }
      if (this.validarTexto(this.form.motivo) !== true) return false;
      if (this.form.anexo && this.validarArquivo(this.form.anexo) !== true) return false;
      return true;
    },
  },
  created() {
    if (this.podeEscolherColaborador) this.carregarColaboradores();
    this.carregarPendentes();
  },
  methods: {
    async carregarColaboradores() {
      try {
        this.carregandoColaboradores = true;
        const lista = await this.$services.colaboradorPontosService.listarColaboradores();
        this.colaboradores = (Array.isArray(lista) ? lista : []).filter(
          (c) => c.id !== this.user?.id
        );
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoColaboradores = false;
      }
    },
    async carregarPendentes() {
      this.atrasoSelecionado = null;
      this.pendentes = [];
      try {
        this.carregandoPendentes = true;
        const resp = await this.$services.colaboradorPontosService.listarAtrasosPendentes(
          this.colaboradorSelecionado?.id
        );
        this.pendentes = Array.isArray(resp?.pendentes) ? resp.pendentes : [];
        // Abre já com o atraso indicado na URL (?batida=) ou, se só houver um, com ele.
        const batidaId = Number(this.$route.query.batida);
        this.atrasoSelecionado =
          this.pendentes.find((a) => a.colaborador_batida_id === batidaId) ||
          (this.pendentes.length === 1 ? this.pendentes[0] : null);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoPendentes = false;
      }
    },
    diaDaSemana(data) {
      if (!data) return "";
      const [ano, mes, dia] = data.split("-").map(Number);
      return new Date(ano, mes - 1, dia).toLocaleDateString("pt-BR", { weekday: "long" });
    },
    validarTexto(v) {
      if (!v) return true;
      if (/\p{Emoji_Presentation}/u.test(v)) return "Emojis não são permitidos";
      if (/[<>{}[\]\\|^~`]/u.test(v)) return "Caracteres especiais não são permitidos";
      return true;
    },
    validarArquivo(file) {
      if (!file) return true;
      const tiposPermitidos = ["application/pdf", "image/jpeg", "image/png"];
      const extensoesPermitidas = [".pdf", ".jpg", ".jpeg", ".png"];
      const ext = file.name ? `.${file.name.split(".").pop().toLowerCase()}` : "";
      if (!tiposPermitidos.includes(file.type) && !extensoesPermitidas.includes(ext)) {
        return "Apenas PDF, JPG ou PNG são permitidos";
      }
      if (file.size > 10 * 1024 * 1024) return "O arquivo excede o limite máximo de 10 MB";
      return true;
    },
    async enviar() {
      if (!this.$refs.formRef.validate() || !this.podeEnviar) return;

      const atraso = this.atrasoSelecionado;
      const formData = new FormData();
      formData.append("colaborador_batida_id", atraso.colaborador_batida_id);
      formData.append("data", atraso.data);
      formData.append("hora_prevista", atraso.hora_prevista || "");
      formData.append("hora_registrada", atraso.hora_registrada || "");
      formData.append("minutos_atraso", atraso.minutos_atraso || 0);
      formData.append("motivo", this.form.motivo.trim());
      if (this.form.anexo) formData.append("anexo", this.form.anexo);
      if (this.colaboradorSelecionado) {
        formData.append("colaborador_id", this.colaboradorSelecionado.id);
      }

      try {
        this.enviando = true;
        await this.$services.colaboradorPontosService.enviarAtrasoJustificado(formData);
        if (this.colaboradorSelecionado) {
          this.$toast.success(
            `Justificativa registrada e aprovada para ${this.colaboradorSelecionado.name}.`
          );
          this.$router.push({ name: "colaboradores.atrasosJustificados.gerenciar" });
        } else {
          this.$toast.success("Justificativa de atraso enviada com sucesso!");
          this.$router.push({ name: "colaboradores.atrasosJustificados" });
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
.atraso-card {
  cursor: pointer;
  border-radius: 10px !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.atraso-card:hover {
  border-color: var(--brand-border) !important;
}

.atraso-card--ativo {
  border: 2px solid var(--brand) !important;
  background-color: var(--brand-soft) !important;
}
</style>
